import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { experts } from "../lib/supportApi";
import type { ExpertApplication, Engagement } from "../lib/supportApi";
import { useApi } from "../lib/useApi";
import { useAction } from "./ProjectTools";
import { Button, CardSkeleton } from "../components/ui";

export default function Experts() {
  const { data, error, loading } = useApi(() => experts.directory(), []);
  const projects = useApi(() => api.projects(), []);
  const [query, setQuery] = useState(""),
    [project, setProject] = useState(""),
    [role, setRole] = useState("viewer");
  const { busy, run, feedback } = useAction(() => {});
  return (
    <>
      <header className="page-head">
        <div>
          <p className="platform-eyebrow">TALARICLOUD EXPERTS</p>
          <h1>Build with an approved expert</h1>
          <p className="subtitle">
            Find help with deployments, databases, and application development.
          </p>
        </div>
        <Link className="btn" to="/expert-program">
          Apply as an expert
        </Link>
      </header>
      <p className="platform-notice">
        TalariCloud certification reflects our platform review. Experts receive
        access only to platforms you invite them to. You control their role and
        can revoke access in your team settings.
      </p>
      {error && <p className="alert error">{error}</p>}
      {loading && !data && <CardSkeleton />}
      {feedback}
      <section className="card advanced-form">
        <label>
          Search specialties or languages
          <input
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
        </label>
        <label>
          Invite to platform
          <select value={project} onChange={(e) => setProject(e.target.value)}>
            <option value="">Select your platform</option>
            {projects.data
              ?.filter((p) => !p.role || p.role === "owner")
              .map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}
                </option>
              ))}
          </select>
        </label>
        <label>
          Access role
          <select value={role} onChange={(e) => setRole(e.target.value)}>
            <option value="viewer">Viewer</option>
            <option value="developer">Developer</option>
          </select>
        </label>
      </section>
      <div className="platform-grid" style={{ marginTop: 24 }}>
        {data
          ?.filter((e) =>
            `${e.display_name} ${e.specialties.join(" ")} ${e.languages.join(" ")}`
              .toLowerCase()
              .includes(query.toLowerCase()),
          )
          .map((e) => (
            <article className="platform-tile" key={e.id}>
              <p className="platform-role">
                TalariCloud certified · {e.availability}
              </p>
              <h2>{e.display_name}</h2>
              <p>{e.bio}</p>
              <p><strong>{e.lifetime_projects}</strong> lifetime projects · {e.completed_projects} completed · {e.active_projects} active</p>
              <p>{e.average_rating === null ? "No client ratings yet" : `★ ${e.average_rating.toFixed(1)} / 5 · ${e.rating_count} verified client ratings`}</p>
              {e.history.length > 0 && <details><summary>Completed projects and reviews</summary>
                {e.history.map((h, i) => <div className="workspace-dependency" key={i}><div>
                  <strong>{h.project_title}</strong><p className="tiny muted">Completed {new Date(h.completed_at).toLocaleDateString()}</p>
                  {h.rating !== null && <p>★ {h.rating} / 5</p>}{h.review && <p>{h.review}</p>}
                </div></div>)}
              </details>}
              <p className="tiny muted">
                {e.experience_years} years experience · {e.languages.join(", ")}
              </p>
              <div className="platform-component-summary">
                {e.specialties.map((s) => (
                  <span key={s}>{s}</span>
                ))}
              </div>
              {e.portfolio_url && (
                <a href={e.portfolio_url} target="_blank" rel="noreferrer">
                  View portfolio ↗
                </a>
              )}
              <Button
                disabled={busy || !project || e.availability === "unavailable"}
                onClick={() =>
                  void run(
                    () => experts.invite(e.id, project, role),
                    "Invitation sent to the expert’s client requests",
                  )
                }
              >
                Invite expert
              </Button>
            </article>
          ))}
      </div>
      <Engagements />
      {data?.length === 0 && (
        <section className="empty">
          <h2>The expert directory is opening soon</h2>
          <p>
            Approved experts who choose to publish their profiles will appear
            here.
          </p>
          <Link to="/expert-program">Apply to the program →</Link>
        </section>
      )}
    </>
  );
}

export function ExpertProgram() {
  const { data, error, reload, loading } = useApi(() => experts.application(), []);
  const pending = useApi(() => experts.pending(), [], 30_000);
  const [name, setName] = useState(""),
    [bio, setBio] = useState(""),
    [skills, setSkills] = useState(""),
    [languages, setLanguages] = useState("English"),
    [url, setUrl] = useState(""),
    [years, setYears] = useState(0),
    [listed, setListed] = useState(false);
  const navigate = useNavigate();
  const { busy, run, feedback } = useAction(() => {
    reload();
    pending.reload();
  });
  return (
    <>
      <header className="page-head">
        <div>
          <p className="platform-eyebrow">EXPERT PROGRAM</p>
          <h1>Help clients succeed on TalariCloud</h1>
          <p className="subtitle">
            Apply, complete a review, and receive project invitations from
            clients.
          </p>
        </div>
        <Link to="/experts">Expert directory →</Link>
      </header>
      {error && <p className="alert error">{error}</p>}
      {loading && !data && <CardSkeleton />}
      {feedback}
      {data && (
        <section className="card">
          <h2>Application: {data.status.replaceAll("_", " ")}</h2>
          {data.review_note && <p>{data.review_note}</p>}
          <p className="tiny muted">
            Certification is approval by TalariCloud, not an external
            professional license.
          </p>
        </section>
      )}
      {data?.status === "approved" ? (
        <section className="card stack">
          <h2>Your certified profile</h2>
          <p>{data.bio}</p>
          <div className="advanced-form">
            <label>
              Availability
              <select
                value={data.availability}
                disabled={busy}
                onChange={(e) =>
                  void run(
                    () => experts.availability(e.target.value, data.listed),
                    "Availability updated",
                  )
                }
              >
                {["available", "limited", "unavailable"].map((v) => (
                  <option key={v}>{v}</option>
                ))}
              </select>
            </label>
            <label className="row">
              <input
                type="checkbox"
                checked={data.listed}
                disabled={busy}
                onChange={(e) =>
                  void run(
                    () =>
                      experts.availability(data.availability, e.target.checked),
                    "Directory preference saved",
                  )
                }
              />
              List my approved profile publicly
            </label>
          </div>
          <Link to="/support">
            Contact support to update certified profile details →
          </Link>
        </section>
      ) : (
        data?.status !== "suspended" && (
          <form
            className="card stack"
            onSubmit={(e) => {
              e.preventDefault();
              void run(
                () =>
                  experts.submit({
                    display_name: name,
                    bio,
                    specialties: skills
                      .split(",")
                      .map((s) => s.trim())
                      .filter(Boolean),
                    languages: languages
                      .split(",")
                      .map((s) => s.trim())
                      .filter(Boolean),
                    portfolio_url: url || null,
                    experience_years: years,
                    availability: "available",
                    listed,
                  }),
                "Application submitted for operator review",
              );
            }}
          >
            <div className="advanced-form">
              <label>
                Professional name
                <input
                  required
                  minLength={2}
                  maxLength={100}
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                />
              </label>
              <label>
                Years of experience
                <input
                  required
                  type="number"
                  min={0}
                  max={60}
                  value={years}
                  onChange={(e) => setYears(Number(e.target.value))}
                />
              </label>
            </div>
            <label>
              Professional statement
              <textarea
                required
                minLength={40}
                maxLength={3000}
                rows={5}
                value={bio}
                onChange={(e) => setBio(e.target.value)}
                placeholder="Describe your experience and how you help clients."
              />
            </label>
            <div className="advanced-form">
              <label>
                Specialties (comma-separated)
                <input
                  required
                  value={skills}
                  onChange={(e) => setSkills(e.target.value)}
                  placeholder="Python, DevOps, PostgreSQL"
                />
              </label>
              <label>
                Languages (comma-separated)
                <input
                  required
                  value={languages}
                  onChange={(e) => setLanguages(e.target.value)}
                />
              </label>
              <label>
                Portfolio URL
                <input
                  type="url"
                  value={url}
                  onChange={(e) => setUrl(e.target.value)}
                />
              </label>
            </div>
            <label className="row">
              <input
                type="checkbox"
                checked={listed}
                onChange={(e) => setListed(e.target.checked)}
              />
              Publish this profile in the directory if approved
            </label>
            <p className="tiny muted">
              A verified account email is required. TalariCloud reviews
              applications and may request more information.
            </p>
            <Button type="submit" loading={busy}>
              Submit application
            </Button>
          </form>
        )
      )}
      <section className="card" style={{ marginTop: 24 }}>
        <h2>Client invitations</h2>
        {pending.error && <p className="alert error">{pending.error}</p>}
        {pending.data?.length === 0 && (
          <p className="muted">
            New client requests will appear here after certification.
          </p>
        )}
        {pending.data?.map((i) => (
          <div className="workspace-dependency" key={i.id}>
            <span>
              {i.project_name} · {i.role}
            </span>
            <Button
              disabled={busy}
              onClick={() =>
                void run(async () => {
                  const result = await experts.accept(i.id);
                  navigate(`/projects/${result.project_id}/workspace`);
                }, "Client invitation accepted")
              }
            >
              Accept invitation
            </Button>
          </div>
        ))}
        <Link to="/projects">Open your assigned platforms →</Link>
      </section>
      <Engagements />
    </>
  );
}

function Engagements() {
  const { data, error, reload, loading } = useApi(() => experts.engagements(), [], 30_000);
  return <section className="stack" style={{ marginTop: 24 }}><h2>Your expert projects</h2>
    <p className="muted">Lifetime counts reflect distinct projects accepted through TalariCloud. Only the client can confirm completion and leave a rating.</p>
    {error && <p className="alert error">{error}</p>}
      {loading && !data && <CardSkeleton />}
    {data?.length === 0 && <p>No expert projects yet.</p>}
    {data?.map(e => <EngagementCard key={e.id} engagement={e} reload={reload} />)}
  </section>;
}
function EngagementCard({ engagement: e, reload }: { engagement: Engagement; reload: () => void }) {
  const [rating, setRating] = useState(e.rating ?? 5);
  const [review, setReview] = useState(e.review ?? "");
  const [publicTitle, setPublicTitle] = useState(e.public_title);
  const { busy, run, feedback } = useAction(reload);
  return <article className="card stack"><h3>{e.project_title}</h3><p>{e.status}</p>{feedback}
    {e.project_id && e.status === "active" && <Link to={`/projects/${e.project_id}/workspace`}>Open project →</Link>}
    {e.is_client && e.status === "active" && <>
      <label className="row"><input type="checkbox" checked={publicTitle} onChange={event => setPublicTitle(event.target.checked)} />Show this project’s name in the expert’s public history</label>
      <p className="tiny muted">Completing the project ends the expert’s project access and enables your review.</p>
      <Button disabled={busy} onClick={() => void run(() => experts.complete(e.id, publicTitle), "Project completed; expert access ended")}>Confirm project completed</Button>
    </>}
    {e.is_client && e.status === "completed" && <form className="stack" onSubmit={event => { event.preventDefault(); void run(() => experts.rate(e.id, rating, review), "Client review saved"); }}>
      <label>Rating<select value={rating} onChange={event => setRating(Number(event.target.value))}>{[5,4,3,2,1].map(v => <option key={v} value={v}>{v} / 5</option>)}</select></label>
      <label>Public review<textarea required minLength={5} maxLength={2000} value={review} onChange={event => setReview(event.target.value)} /></label>
      <p className="tiny muted">Reviews are public. Keep account details, payment information, and secrets out of your review.</p>
      <Button disabled={busy} type="submit">{e.rating ? "Update review" : "Publish review"}</Button>
    </form>}
    {!e.is_client && e.rating !== null && <p>★ {e.rating} / 5 — {e.review}</p>}
  </article>;
}

export function ExpertReview() {
  const { data, error, reload, loading } = useApi(
    () => experts.applications(),
    [],
    30_000,
  );
  return (
    <>
      <header className="page-head">
        <div>
          <p className="platform-eyebrow">EXPERT CERTIFICATION</p>
          <h1>Review expert applications</h1>
          <p className="subtitle">
            Verify experience and portfolio evidence before granting TalariCloud
            certification.
          </p>
        </div>
      </header>
      {error && <p className="alert error">{error}</p>}
      {loading && !data && <CardSkeleton />}
      {data?.length === 0 && <p className="platform-notice">No expert applications to review.</p>}
      <div className="stack">
        {data?.map((e) => (
          <ReviewCard key={e.id} expert={e} reload={reload} />
        ))}
      </div>
    </>
  );
}
function ReviewCard({
  expert: e,
  reload,
}: {
  expert: ExpertApplication;
  reload: () => void;
}) {
  const [status, setStatus] = useState(e.status),
    [note, setNote] = useState("");
  const { busy, run, feedback } = useAction(reload);
  return (
    <article className="card stack">
      <div className="row">
        <h2>{e.display_name}</h2>
        <span className="grow" />
        <span className="platform-role">{e.status}</span>
      </div>
      <p>
        {e.email} · {e.experience_years} years · {e.specialties.join(", ")}
      </p>
      <p>{e.bio}</p>
      {e.portfolio_url && (
        <a href={e.portfolio_url} target="_blank" rel="noreferrer">
          Review portfolio ↗
        </a>
      )}
      {e.review_note && <p className="platform-notice">{e.review_note}</p>}
      {feedback}
      <form
        className="stack"
        onSubmit={(event) => {
          event.preventDefault();
          void run(
            () => experts.review(e.id, status, note),
            "Certification review recorded",
          );
        }}
      >
        <label>
          Decision
          <select
            value={status}
            onChange={(event) => setStatus(event.target.value)}
          >
            {[
              "under_review",
              "needs_information",
              "approved",
              "rejected",
              "suspended",
            ].map((v) => (
              <option key={v} value={v}>
                {v.replaceAll("_", " ")}
              </option>
            ))}
          </select>
        </label>
        <label>
          Applicant-visible review note
          <textarea
            required
            minLength={5}
            maxLength={3000}
            value={note}
            onChange={(event) => setNote(event.target.value)}
          />
        </label>
        <p className="tiny muted">
          Removing approval revokes project access granted through expert
          invitations. Ordinary team memberships are managed by clients.
        </p>
        <Button type="submit" loading={busy}>
          Record decision
        </Button>
      </form>
    </article>
  );
}

